import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Package, CheckCircle, Repeat, Clock, Pause, XCircle, FileText,
  Truck, PackageCheck, RotateCcw, Loader, X, Users, ChevronRight,
  ChevronDown, CalendarDays, RefreshCw, Filter, Send, ShieldCheck, Activity,
  Search, Layers, Download,
  Hourglass, Banknote, CreditCard, Wallet, AlertTriangle,
} from 'lucide-react';
import { dashboardApi, teamsApi, employeesApi, floorsApi, fetchApiDirect } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';
import { message, DatePicker } from 'antd';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 'All'];

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

const resolveDateRange = (preset, customStart = '', customEnd = '') => {
  const now = new Date();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const fmtStart = (d) => { d.setHours(0, 0, 0, 0); return fmt(d); };
  const fmtEnd = (d) => { d.setHours(23, 59, 59, 999); return fmt(d); };

  switch (preset) {
    case 'today':
      return { startDate: fmtStart(new Date(now)), endDate: fmtEnd(new Date(now)) };
    case 'yesterday': {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return { startDate: fmtStart(y), endDate: fmtEnd(new Date(y)) };
    }
    case 'this_week': {
      const d = now.getDay();
      const diff = now.getDate() - d + (d === 0 ? -6 : 1);
      const s = new Date(now);
      s.setDate(diff);
      return { startDate: fmtStart(s), endDate: fmtEnd(new Date(now)) };
    }
    case 'this_month':
      return {
        startDate: fmtStart(new Date(now.getFullYear(), now.getMonth(), 1)),
        endDate: fmtEnd(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
      };
    case 'this_year':
      return {
        startDate: fmtStart(new Date(now.getFullYear(), 0, 1)),
        endDate: fmtEnd(new Date(now.getFullYear(), 11, 31)),
      };
    case 'custom':
      return { startDate: customStart || '', endDate: customEnd || '' };
    default:
      return { startDate: fmtStart(new Date(now)), endDate: fmtEnd(new Date(now)) };
  }
};

const STAT_KEYS = [
  'total', 'confirmed', 'verified', 'process', 'readyToShip', 'pending',
  'hold', 'rejected', 'draft', 'dispatched', 'shipped', 'inTransit',
  'ofd', 'delivered', 'rto',
  'approvalPending', 'cod', 'prepaid', 'partPayment', 'ndr',
];

const ICON_MAP = {
  total: Package, confirmed: ShieldCheck, verified: CheckCircle, process: Repeat,
  readyToShip: PackageCheck, pending: Clock, hold: Pause, rejected: XCircle,
  draft: FileText, dispatched: Truck, shipped: Send, inTransit: Activity,
  ofd: Truck, delivered: PackageCheck, rto: RotateCcw,
  approvalPending: Hourglass, cod: Banknote, prepaid: CreditCard,
  partPayment: Wallet, ndr: AlertTriangle,
};

const COLOR_MAP = {
  total: 'bg-[#4B9EF9]', confirmed: 'bg-[#10B981]', verified: 'bg-[#4CAF50]',
  process: 'bg-[#FFC107]', readyToShip: 'bg-[#0EA5E9]', pending: 'bg-[#FF9800]',
  hold: 'bg-[#78909C]', rejected: 'bg-[#F06292]', draft: 'bg-[#A1887F]',
  dispatched: 'bg-[#3B82F6]', shipped: 'bg-[#6366F1]', inTransit: 'bg-[#8B5CF6]',
  ofd: 'bg-[#00BCD4]', delivered: 'bg-[#26A69A]', rto: 'bg-[#7E57C2]',
  approvalPending: 'bg-[#F59E0B]', cod: 'bg-[#16A34A]', prepaid: 'bg-[#2563EB]',
  partPayment: 'bg-[#DB2777]', ndr: 'bg-[#EF4444]',
};

const LABEL_MAP = {
  total: 'Total Orders', confirmed: 'Confirmed Orders', verified: 'Verified Orders',
  process: 'Process Orders', readyToShip: 'Ready To Ship', pending: 'Pending Orders',
  hold: 'Hold Orders', rejected: 'Rejected Orders', draft: 'Draft Orders',
  dispatched: 'Dispatched Orders', shipped: 'Shipped Orders', inTransit: 'In Transit',
  ofd: 'OFD Orders', delivered: 'Delivered Orders', rto: 'RTO Orders',
  approvalPending: 'Approval Pending', cod: 'COD Orders', prepaid: 'Prepaid Orders',
  partPayment: 'Part Payment', ndr: 'NDR Orders',
};

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtAvg = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const getCell = (obj, ...keys) => {
  for (const k of keys) if (obj?.[k] !== undefined) return obj[k];
  return 0;
};

const normalizeOverview = (data) => {
  if (!data) return {};
  if (data.total && typeof data.total === 'object') return data;
  return {
    total: { count: data.totalOrders, totalValue: data.totalValue, avgValue: data.totalAvgValue || data.avgValue },
    confirmed: { count: data.confirmedOrders, totalValue: data.confirmedValue, avgValue: data.confirmedAvgValue },
    verified: { count: data.verifiedOrders, totalValue: data.verifiedValue, avgValue: data.verifiedAvgValue },
    process: { count: data.processOrders, totalValue: data.processValue, avgValue: data.processAvgValue },
    readyToShip: { count: data.readyToShipOrders, totalValue: data.readyToShipValue, avgValue: data.readyToShipAvgValue },
    pending: { count: data.pendingOrders, totalValue: data.pendingValue, avgValue: data.pendingAvgValue },
    hold: { count: data.holdOrders, totalValue: data.holdValue, avgValue: data.holdAvgValue },
    rejected: { count: data.rejectedOrders, totalValue: data.rejectedValue, avgValue: data.rejectedAvgValue },
    draft: { count: data.draftOrders, totalValue: data.draftValue, avgValue: data.draftAvgValue },
    dispatched: { count: data.dispatchedOrders, totalValue: data.dispatchedValue, avgValue: data.dispatchedAvgValue },
    shipped: { count: data.shippedOrders, totalValue: data.shippedValue, avgValue: data.shippedAvgValue },
    inTransit: { count: data.inTransitOrders, totalValue: data.inTransitValue, avgValue: data.inTransitAvgValue },
    ofd: { count: data.ofdOrders, totalValue: data.ofdValue, avgValue: data.ofdAvgValue },
    delivered: { count: data.deliveredOrders, totalValue: data.deliveredValue, avgValue: data.deliveredAvgValue },
    rto: { count: data.rtoOrders, totalValue: data.rtoValue, avgValue: data.rtoAvgValue },
    approvalPending: { count: data.approvalPendingOrders, totalValue: data.approvalPendingValue, avgValue: data.approvalPendingAvgValue },
    cod: { count: data.codOrderCount, totalValue: data.codTotalValue, avgValue: data.codAvgValue },
    prepaid: { count: data.prepaidOrderCount, totalValue: data.prepaidOrdersValue, avgValue: data.prepaidAvgValue },
    partPayment: { count: data.partPaymentOrderCount, totalValue: data.partPaymentTotalOrderValue, avgValue: data.partPaymentAvgValue },
    ndr: { count: data.ndrOrders, totalValue: data.ndrValue, avgValue: data.ndrAvgValue },
  };
};

const normalizeEmployeePerformance = (data) => {
  if (!data) return { content: [], totalPages: 1, totalElements: 0, number: 0 };
  if (Array.isArray(data.employees)) {
    const size = data.size || 10;
    const totalCount = data.totalCount || data.employees.length;
    return { content: data.employees, number: data.page ?? 0, totalPages: Math.ceil(totalCount / size), totalElements: totalCount };
  }
  if (Array.isArray(data.content)) return data;
  if (Array.isArray(data)) return { content: data, totalPages: 1, totalElements: data.length, number: 0 };
  return { content: [], totalPages: 1, totalElements: 0, number: 0 };
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ type, count, totalValue, avgValue }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const Icon = ICON_MAP[type] || Package;
  const baseColor = COLOR_MAP[type];
  return (
    <div className={`rounded-[20px] p-5 shadow-sm flex flex-col justify-between h-full min-h-[160px] transition-colors relative overflow-hidden ${isDark ? 'bg-black/20 border border-white/10 text-white' : `${baseColor} text-white`}`}>
      <div className="flex items-start justify-between">
        <div className="flex flex-col">
          <span className="text-[11px] font-bold opacity-80 tracking-widest uppercase mb-1">{LABEL_MAP[type]}</span>
          <span className="text-3xl font-black leading-tight tracking-tight">{fmt(count)}</span>
        </div>
        <div className={`${isDark ? baseColor : 'bg-white/20'} p-3 rounded-[14px] flex items-center justify-center shrink-0 shadow-sm`}>
          <Icon size={24} className="text-white" strokeWidth={2.5} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-6">
        <div className="flex flex-col gap-1.5">
          <div className="text-[9px] font-extrabold uppercase tracking-wider opacity-70 ml-1">Total Value</div>
          <div className={`${isDark ? 'bg-black/40 text-gray-100' : 'bg-white text-black'} rounded-xl px-3 py-2 text-[13px] font-black flex items-center shadow-inner`}>₹ {fmt(totalValue)}</div>
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="text-[9px] font-extrabold uppercase tracking-wider opacity-70 ml-1">Avg Value</div>
          <div className={`${isDark ? 'bg-black/40 text-gray-100' : 'bg-white text-black'} rounded-xl px-3 py-2 text-[13px] font-black flex items-center shadow-inner`}>₹ {fmtAvg(avgValue)}</div>
        </div>
      </div>
    </div>
  );
};

// ─── Employee Detail Modal ────────────────────────────────────────────────────
const EmployeeDetailModal = ({ isOpen, onClose, employee }) => {
  const { currentTheme } = useTheme();
  if (!isOpen || !employee) return null;
  const isDark = currentTheme.textColor === '#FFFFFF';
  const name = employee.name || employee.employeeName || 'Employee';
  const totalAmount = getCell(employee, 'totalAmount', 'totalAmt', 'grandTotal');
  const totalOrders = getCell(employee, 'total', 'totalOrders', 'orderCount');
  const avgVal = employee.avgTotalAmount ?? employee.avgTotal ?? employee.avgOrderValue ?? 0;
  const statItems = [
    { label: 'Confirmed', value: getCell(employee, 'confirmedOrders', 'approvalConfirmed', 'confirmed'), lightBg: 'bg-emerald-50/50', darkBg: 'bg-emerald-500/5', color: 'text-emerald-600' },
    { label: 'Verified', value: getCell(employee, 'verified', 'complete', 'verifiedOrders'), lightBg: 'bg-green-50', darkBg: 'bg-green-500/10', color: 'text-green-600' },
    { label: 'Draft', value: getCell(employee, 'draft', 'draftOrders'), lightBg: 'bg-stone-50', darkBg: 'bg-stone-500/10', color: 'text-stone-500' },
    { label: 'Process', value: getCell(employee, 'process', 'procces', 'processing', 'processOrders'), lightBg: 'bg-amber-50', darkBg: 'bg-amber-500/10', color: 'text-amber-500' },
    { label: 'Ready To Ship', value: getCell(employee, 'readyToShip', 'readyToShipOrders'), lightBg: 'bg-sky-50', darkBg: 'bg-sky-500/10', color: 'text-sky-500' },
    { label: 'Pending', value: getCell(employee, 'pending', 'pendingOrders'), lightBg: 'bg-orange-50', darkBg: 'bg-orange-500/10', color: 'text-orange-500' },
    { label: 'Hold', value: getCell(employee, 'hold', 'onHold', 'holdOrders'), lightBg: 'bg-slate-50', darkBg: 'bg-slate-500/10', color: 'text-slate-500' },
    { label: 'Rejected', value: getCell(employee, 'rejected', 'cancelled', 'rejectedOrders'), lightBg: 'bg-rose-50', darkBg: 'bg-rose-500/10', color: 'text-rose-500' },
    { label: 'Dispatched', value: getCell(employee, 'dispatched', 'dispatch', 'dispatchedOrders'), lightBg: 'bg-blue-50', darkBg: 'bg-blue-500/10', color: 'text-blue-500' },
    { label: 'Shipped', value: getCell(employee, 'shipped', 'shippedOrders'), lightBg: 'bg-indigo-50', darkBg: 'bg-indigo-500/10', color: 'text-indigo-500' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`rounded-[32px] w-full max-w-lg shadow-2xl overflow-hidden ${isDark ? 'bg-[#121212] border border-white/10 text-white' : 'bg-white text-gray-800'}`}>
        <div className="p-6 text-white flex justify-between items-center" style={{ background: `linear-gradient(to right, ${currentTheme.color}, ${currentTheme.color}dd)` }}>
          <div>
            <h2 className="text-2xl font-black">{name}</h2>
            <p className="text-xs opacity-80 mt-0.5">Employee Performance Overview</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors"><X size={20} /></button>
        </div>
        <div className="p-6 max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className={`p-5 rounded-2xl border flex items-center justify-between transition-colors ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-100'}`}>
              <div className="flex-1 border-r border-gray-200 dark:border-white/10 pr-4 text-center">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Total</p>
                <p className="text-2xl font-black" style={{ color: currentTheme.color }}>{totalOrders}</p>
              </div>
              <div className="flex-1 pl-4 text-center">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Value</p>
                <p className={`text-2xl font-black ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>₹{fmt(totalAmount)}</p>
              </div>
            </div>
            <div className={`p-5 rounded-2xl border transition-colors ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-100'}`}>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 text-center">Avg Order Value</p>
              <p className={`text-2xl font-black text-center ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>₹{fmtAvg(avgVal)}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {statItems.map((item, i) => (
              <div key={i} className={`py-5 px-4 min-h-[92px] rounded-2xl flex flex-col items-center justify-center text-center transition-all hover:scale-[1.02] border border-transparent ${isDark ? `${item.darkBg} hover:border-white/5` : `${item.lightBg} hover:shadow-sm`}`}>
                <p className={`text-2xl font-black mb-0.5 ${isDark ? 'text-white' : 'text-gray-800'}`}>{item.value}</p>
                <p className={`text-[10px] font-bold uppercase tracking-wide ${item.color}`}>{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Team Members Modal ───────────────────────────────────────────────────────
const TeamMembersModal = ({ isOpen, onClose, teamName, employees, onEmployeeClick }) => {
  const { currentTheme } = useTheme();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[32px] w-full max-w-2xl shadow-2xl overflow-hidden">
        <div className="p-6 text-white flex justify-between items-center" style={{ background: `linear-gradient(to right, ${currentTheme.color}, ${currentTheme.color}dd)` }}>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl"><Users size={24} /></div>
            <div>
              <h2 className="text-xl font-bold">{teamName}</h2>
              <p className="text-xs opacity-80">{employees.length} Employees contributing</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors"><X size={24} /></button>
        </div>
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {employees.length > 0 ? employees.map((emp, idx) => {
              const eName = emp.name || emp.employeeName || 'Employee';
              const eTotal = getCell(emp, 'total', 'totalOrders', 'orderCount');
              return (
                <button key={idx} onClick={() => onEmployeeClick(emp)}
                  className="flex items-center justify-between p-4 rounded-2xl border border-gray-100 hover:bg-[#F0FDF4] transition-all group active:scale-95 text-left"
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = currentTheme.color}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#f3f4f6'}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center font-bold" style={{ color: currentTheme.color }}>
                      {eName.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">{eName}</h3>
                      <p className="text-[10px] text-gray-500 uppercase font-bold">Total Orders: {eTotal}</p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-gray-300 group-hover:translate-x-1 transition-all" />
                </button>
              );
            }) : (
              <div className="col-span-2 py-12 text-center text-gray-400">No employee data found for this team.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Searchable Select ────────────────────────────────────────────────────────
const SearchableSelect = ({ options, value, onChange, placeholder, label, isDark, currentTheme, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => String(opt.id) === String(value));
  const getOptionLabel = (opt) => opt.name || opt.teamName || opt.floorName || opt.employeeName || `ID: ${opt.id}`;
  const filteredOptions = options.filter(opt => getOptionLabel(opt).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex flex-col gap-1">
        {label && <label className="text-[10px] text-gray-400 font-bold uppercase tracking-wider ml-1">{label}</label>}
        <div onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`cursor-pointer border rounded-xl px-4 py-2 text-sm min-w-[200px] flex justify-between items-center transition-all duration-200 ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:shadow-md'} ${isDark ? 'bg-black/40 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-700'}`}
          style={{ borderColor: isOpen ? currentTheme.color : undefined }}>
          <span className="truncate font-medium">{selectedOption ? getOptionLabel(selectedOption) : placeholder}</span>
          <div className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
            <ChevronRight size={14} className="rotate-90 opacity-50" />
          </div>
        </div>
      </div>
      {isOpen && (
        <div className={`absolute z-[100] mt-2 w-full min-w-[240px] rounded-2xl shadow-2xl border overflow-hidden ${isDark ? 'bg-[#121212] border-white/10' : 'bg-white border-gray-100'}`}>
          <div className={`sticky top-0 z-10 p-2.5 border-b ${isDark ? 'bg-[#121212]/90 backdrop-blur-md border-white/5' : 'bg-white/90 backdrop-blur-md border-gray-100'}`}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-100'}`}>
              <Search size={14} className="text-gray-400" />
              <input autoFocus type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
                className={`w-full bg-transparent text-sm outline-none placeholder:text-gray-500 ${isDark ? 'text-white' : 'text-gray-900'}`} />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1.5">
            {!search && (
              <div
                className={`mx-1.5 px-3 py-2 rounded-lg text-sm cursor-pointer transition-all mb-0.5 flex items-center justify-between ${!value ? 'font-bold' : 'hover:bg-gray-50'}`}
                style={{ backgroundColor: !value ? `${currentTheme.color}15` : undefined, color: !value ? currentTheme.color : (isDark ? '#D1D5DB' : '#111827') }}
                onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}>
                <span>{placeholder}</span>
                {!value && <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: currentTheme.color }} />}
              </div>
            )}
            {filteredOptions.length > 0 ? filteredOptions.map((opt) => (
              <div key={opt.id}
                className={`mx-1.5 px-3 py-2 rounded-lg text-sm cursor-pointer transition-all mb-0.5 flex items-center justify-between ${String(opt.id) === String(value) ? 'font-bold' : 'hover:bg-gray-50'}`}
                style={{ backgroundColor: String(opt.id) === String(value) ? `${currentTheme.color}15` : undefined, color: String(opt.id) === String(value) ? currentTheme.color : (isDark ? '#D1D5DB' : '#111827') }}
                onClick={() => { onChange(opt.id); setIsOpen(false); setSearch(''); }}>
                <span className="truncate">{getOptionLabel(opt)}</span>
                {String(opt.id) === String(value) && <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: currentTheme.color }} />}
              </div>
            )) : (
              <div className="px-3 py-8 text-center"><p className="text-xs text-gray-400">No results matching "{search}"</p></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Filter Bar ───────────────────────────────────────────────────────────────
const FilterBar = ({
  selectedDateRange, selectedDateRangeLabel, onDateRangeSelect,
  selectedTeamId, onTeamChange, selectedEmployeeId, onEmployeeChange,
  selectedFloorId, onFloorChange, teams, employees, floors,
  teamsLoading, empsLoading,
  onApply, onClear, loading, isAdmin, userTeamId, userTeamName,
}) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const dateDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target)) {
        setIsDateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getFloorLabel = (id) => { const f = floors.find(f => String(f.id) === String(id)); return f ? (f.name || f.floorName || id) : id; };
  const getTeamLabel = (id) => { const t = teams.find(t => String(t.id) === String(id)); return t ? (t.teamName || t.name || id) : id; };
  const getEmpLabel = (id) => { const e = employees.find(e => String(e.id) === String(id)); return e ? (e.name || e.employeeName || id) : id; };

  return (
    <div className={`rounded-2xl px-5 py-4 shadow-sm border mb-6 ${isDark ? 'bg-black/20 border-white/10' : 'bg-white border-gray-100'}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {/* Date Dropdown */}
        <div className="relative" ref={dateDropdownRef}>
          <button
            onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
            className={`flex items-center border rounded-full px-4 py-2 shadow-sm cursor-pointer transition-all ${isDark ? 'bg-black/40 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
            style={{ borderColor: isDateDropdownOpen ? currentTheme.color : undefined }}
          >
            <CalendarDays className="w-5 h-5 text-orange-400 mr-2 shrink-0" />
            <span className="text-[14px] font-semibold">{selectedDateRangeLabel}</span>
            <ChevronDown className={`w-4 h-4 ml-2 transition-transform shrink-0 ${isDateDropdownOpen ? 'rotate-180' : ''}`} style={{ color: currentTheme.color }} />
          </button>

          {isDateDropdownOpen && (
            <div className={`absolute left-0 top-full mt-2 rounded-2xl shadow-2xl border py-2 z-50 w-56 overflow-hidden ${isDark ? 'bg-[#1e1e1e] border-white/10' : 'bg-white border-gray-100'}`}>
              {DATE_RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setIsDateDropdownOpen(false);
                    onDateRangeSelect(opt.value, opt.label);
                  }}
                  className="w-full px-5 py-2.5 text-left text-[14px] transition-colors"
                  style={{
                    backgroundColor: selectedDateRange === opt.value ? `${currentTheme.color}18` : 'transparent',
                    color: selectedDateRange === opt.value ? currentTheme.color : (isDark ? '#E5E7EB' : '#374151'),
                    fontWeight: selectedDateRange === opt.value ? 'bold' : 'normal',
                  }}
                  onMouseEnter={(e) => { if (selectedDateRange !== opt.value) e.currentTarget.style.backgroundColor = `${currentTheme.color}10`; }}
                  onMouseLeave={(e) => { if (selectedDateRange !== opt.value) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <button onClick={onApply} disabled={loading}
          className="text-white text-xs font-bold px-5 py-2.5 rounded-xl transition-all shadow-sm disabled:opacity-60"
          style={{ backgroundColor: currentTheme.color }}>Apply</button>
        <button onClick={onClear} disabled={loading}
          className={`${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} text-xs font-bold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-60`}>
          Clear All
        </button>
        {loading && <Loader size={16} className="animate-spin" style={{ color: currentTheme.color }} />}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Filter size={15} className="shrink-0 opacity-50" />
        {isAdmin && (
          <SearchableSelect label="Floor" options={floors} value={selectedFloorId} placeholder="All Floors"
            isDark={isDark} currentTheme={currentTheme} disabled={false}
            onChange={(val) => { onFloorChange(val); onTeamChange(''); onEmployeeChange(''); }}
          />
        )}
        <div className="flex items-center gap-2">
          {!isAdmin && userTeamId ? (
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold px-3 py-1.5 rounded-xl flex items-center gap-1.5 border"
                style={{ backgroundColor: `${currentTheme.color}18`, borderColor: `${currentTheme.color}40`, color: currentTheme.color }}>
                🔒 {userTeamName || `Team ${userTeamId}`}
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider ml-1">Team</span>
                {teamsLoading && <Loader size={10} className="animate-spin" style={{ color: currentTheme.color }} />}
              </div>
              <SearchableSelect label="" options={teams} value={selectedTeamId}
                placeholder={teamsLoading ? 'Loading teams...' : 'All Teams'}
                isDark={isDark} currentTheme={currentTheme} disabled={teamsLoading}
                onChange={(val) => { onTeamChange(val); onEmployeeChange(''); }}
              />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider ml-1">Employee</span>
            {empsLoading && <Loader size={10} className="animate-spin" style={{ color: currentTheme.color }} />}
          </div>
          <SearchableSelect label="" options={employees} value={selectedEmployeeId}
            placeholder={empsLoading ? 'Loading employees...' : 'All Employees'}
            isDark={isDark} currentTheme={currentTheme} disabled={empsLoading}
            onChange={(val) => { onEmployeeChange(val); }}
          />
        </div>
        {selectedFloorId && (
          <span className="text-xs font-bold px-3 py-1 rounded-full text-white flex items-center gap-1.5 cursor-pointer"
            style={{ backgroundColor: currentTheme.color }} onClick={() => { onFloorChange(''); onTeamChange(''); onEmployeeChange(''); }}>
            <Layers size={10} /> Floor: {getFloorLabel(selectedFloorId)} <X size={12} />
          </span>
        )}
        {selectedTeamId && (
          <span className="text-xs font-bold px-3 py-1 rounded-full text-white flex items-center gap-1.5 cursor-pointer"
            style={{ backgroundColor: currentTheme.color }} onClick={() => { onTeamChange(''); onEmployeeChange(''); }}>
            Team: {getTeamLabel(selectedTeamId)} <X size={12} />
          </span>
        )}
        {selectedEmployeeId && (
          <span className="text-xs font-bold px-3 py-1 rounded-full text-white flex items-center gap-1.5 cursor-pointer"
            style={{ backgroundColor: currentTheme.color }} onClick={() => onEmployeeChange('')}>
            Emp: {getEmpLabel(selectedEmployeeId)} <X size={12} />
          </span>
        )}
      </div>
    </div>
  );
};

const getLoggedInUser = () => {
  try { const raw = localStorage.getItem('user'); return raw ? JSON.parse(raw) : null; }
  catch { return null; }
};

const TABLE_HEADERS = [
  'Employee ID', 'Name', 'Team', 'Confirmed', 'Verified', 'Draft', 'Process', 'Ready To Ship',
  'Pending', 'Hold', 'Rejected', 'Dispatched', 'Shipped',
  'In Transit', 'OFD', 'Delivered', 'RTO', 'Total', 'Avg Total Amt', 'Avg Verified Amt',
];

// ─── Main Analytics Component ─────────────────────────────────────────────────
const Analytics = () => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';

  const loggedInUser = React.useMemo(() => getLoggedInUser(), []);
  const userRole = loggedInUser?.role || '';
  const userTeamId = loggedInUser?.teamId || '';
  const isAdmin = userRole === 'ADMIN';

  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [overview, setOverview] = useState({});
  const [tableData, setTableData] = useState([]);
  const [grandTotal, setGrandTotal] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalPages: 1, totalElements: 0 });

  // ── Date Filters ──
  const initialDateRange = resolveDateRange('today');
  const [selectedDateRange, setSelectedDateRange] = useState('today');
  const [selectedDateRangeLabel, setSelectedDateRangeLabel] = useState('Today');
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);

  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [teamId, setTeamId] = useState(!isAdmin && userTeamId ? String(userTeamId) : '');
  const [employeeId, setEmployeeId] = useState('');
  const [floorId, setFloorId] = useState('');

  const [appliedFilters, setAppliedFilters] = useState({
    startDate: initialDateRange.startDate,
    endDate: initialDateRange.endDate,
    teamId: !isAdmin && userTeamId ? String(userTeamId) : '',
    employeeId: '',
    floorId: '',
  });

  const [floors, setFloors] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [empsLoading, setEmpsLoading] = useState(false);

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [floorsRes, empsRes, teamsRes] = await Promise.all([
          floorsApi.getAllFloors(),
          employeesApi.getAllEmployees(),
          teamsApi.getAllTeams(),
        ]);
        const floorsArr = Array.isArray(floorsRes) ? floorsRes : floorsRes?.content || floorsRes?.floors || [];
        const empsArr = Array.isArray(empsRes) ? empsRes : empsRes?.content || empsRes?.employees || [];
        const rawTeams = Array.isArray(teamsRes) ? teamsRes : teamsRes?.content || teamsRes?.teams || [];
        const teamsArr = rawTeams.map(t => ({
          id: t.id || t.teamId || '',
          name: t.teamName || t.name || '',
          teamName: t.teamName || t.name || '',
        })).filter(t => t.id && (t.name || t.teamName));

        setFloors(floorsArr);
        setAllEmployees(empsArr);
        setEmployees(empsArr);
        setAllTeams(teamsArr);
        setTeams(teamsArr);
      } catch (err) {
        console.error('Failed to load dropdown data:', err);
      }
    };
    loadDropdownData();
  }, []);

  // ── Floor select → Teams fetch dynamically ────────────────────────────────
  const handleFloorSelect = async (val) => {
    setFloorId(val);
    setTeamId('');
    setEmployeeId('');

    if (!val) {
      setTeams(allTeams);
      setEmployees(allEmployees);
      return;
    }

    setTeamsLoading(true);
    try {
      const res = await fetchApiDirect(`/employees/grouped?floorId=${val}`);
      const teamsRaw = Array.isArray(res?.teams) ? res.teams : Array.isArray(res) ? res : [];
      const mappedTeams = teamsRaw.map(t => ({
        id: t.teamId || t.id || '',
        name: t.teamName || t.name || '',
        teamName: t.teamName || t.name || '',
      })).filter(t => t.id && (t.name || t.teamName));
      setTeams(mappedTeams.length > 0 ? mappedTeams : allTeams);

      const allEmpsOfFloor = teamsRaw.flatMap(t =>
        (Array.isArray(t.employees) ? t.employees : []).map(e => ({
          id: e.id || e.empId || '',
          name: e.name || e.empName || e.employeeName || '',
        }))
      ).filter(e => e.id && e.name);
      setEmployees(allEmpsOfFloor.length > 0 ? allEmpsOfFloor : allEmployees);
    } catch (err) {
      console.error('Teams fetch error:', err);
      setTeams(allTeams);
      setEmployees(allEmployees);
    } finally {
      setTeamsLoading(false);
    }
  };

  // ── Team select → Employees fetch dynamically ─────────────────────────────
  const handleTeamSelect = async (val) => {
    setTeamId(val);
    setEmployeeId('');

    if (!val) {
      if (floorId) {
        setEmpsLoading(true);
        try {
          const res = await fetchApiDirect(`/employees/grouped?floorId=${floorId}`);
          const teamsRaw = Array.isArray(res?.teams) ? res.teams : Array.isArray(res) ? res : [];
          const allEmpsOfFloor = teamsRaw.flatMap(t =>
            (Array.isArray(t.employees) ? t.employees : []).map(e => ({
              id: e.id || e.empId || '', name: e.name || e.empName || e.employeeName || '',
            }))
          ).filter(e => e.id && e.name);
          setEmployees(allEmpsOfFloor.length > 0 ? allEmpsOfFloor : allEmployees);
        } catch (err) {
          console.error(err);
          setEmployees(allEmployees);
        } finally {
          setEmpsLoading(false);
        }
      } else {
        setEmployees(allEmployees);
      }
      return;
    }

    setEmpsLoading(true);
    try {
      const params = floorId ? `floorId=${floorId}&teamId=${val}` : `teamId=${val}`;
      const res = await fetchApiDirect(`/employees/grouped?${params}`);
      const empsRaw = Array.isArray(res?.employees)
        ? res.employees
        : Array.isArray(res?.teams?.[0]?.employees)
          ? res.teams[0].employees
          : Array.isArray(res) ? res : [];
      const mapped = empsRaw.map(e => ({
        id: e.id || e.empId || '',
        name: e.name || e.empName || e.employeeName || '',
      })).filter(e => e.id && e.name);

      if (mapped.length > 0) {
        setEmployees(mapped);
      } else {
        const fallback = allEmployees.filter(e => String(e.teamId || e.team?.id) === String(val));
        setEmployees(fallback.length > 0 ? fallback : allEmployees);
      }
    } catch (err) {
      console.error('Employees fetch error:', err);
      const fallback = allEmployees.filter(e => String(e.teamId || e.team?.id) === String(val));
      setEmployees(fallback.length > 0 ? fallback : allEmployees);
    } finally {
      setEmpsLoading(false);
    }
  };

  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const teamEmployeesMap = React.useMemo(() => {
    const map = {};
    tableData.forEach((row) => {
      const tName = row.teamName || row.team || row.teamTitle;
      if (tName) {
        if (!map[tName]) map[tName] = [];
        map[tName].push(row);
      }
    });
    return map;
  }, [tableData]);

  // ── buildParams ───────────────────────────────────────────────────────────
  const buildParams = useCallback(
    (page = 0, size = null, filters = null) => {
      const f = filters ?? appliedFilters;
      const s = size ?? pagination.size;
      const p = {};
      if (f.startDate) p.startDate = f.startDate;
      if (f.endDate) p.endDate = f.endDate;
      if (f.teamId) p.teamId = f.teamId;
      if (f.employeeId) p.employeeId = f.employeeId;
      if (f.floorId) p.floorId = f.floorId;
      p.page = page;
      p.size = s;
      return p;
    },
    [appliedFilters, pagination.size],
  );

  // ── fetchAll ──────────────────────────────────────────────────────────────
  const fetchAll = useCallback(
    async (page = 0, filters = null, size = null) => {
      setLoading(true);
      try {
        const params = buildParams(page, size, filters);
        const [overviewRes, perfRes, grandRes] = await Promise.all([
          dashboardApi.getAnalyticsOverview(params),
          dashboardApi.getEmployeePerformance(params),
          dashboardApi.getGrandTotal(params),
        ]);

        setOverview(normalizeOverview(overviewRes));

        const perf = normalizeEmployeePerformance(perfRes);
        setTableData(perf.content || []);
        setPagination((prev) => ({
          ...prev,
          page: perf.number ?? page,
          totalPages: perf.totalPages ?? 1,
          totalElements: perf.totalElements ?? (perf.content?.length ?? 0),
          size: params.size,
        }));

        setGrandTotal(grandRes);
      } catch (err) {
        console.error('Analytics fetch failed:', err);
      } finally {
        setLoading(false);
      }
    },
    [buildParams],
  );

  // Initial load
  useEffect(() => {
    fetchAll(0);
  }, []); // eslint-disable-line

  // ── Date Range Select handler ─────────────────────────────────────────────
  const handleDateRangeFilter = (rangeType, label) => {
    if (rangeType === 'custom') {
      setShowCustomDateModal(true);
      return;
    }
    setSelectedDateRange(rangeType);
    setSelectedDateRangeLabel(label || 'All View');
    const resolved = resolveDateRange(rangeType);
    setStartDate(resolved.startDate);
    setEndDate(resolved.endDate);

    const newFilters = {
      startDate: resolved.startDate,
      endDate: resolved.endDate,
      teamId,
      employeeId,
      floorId,
    };
    setCurrentPage(1);
    setAppliedFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 0, size: 10 }));
    fetchAll(0, newFilters, 10);
  };

  // ── Custom Date Range Apply ───────────────────────────────────────────────
  const handleCustomDateApply = () => {
    if (!customStartDate || !customEndDate) {
      message.warning('Please select both start and end dates.');
      return;
    }
    if (customEndDate < customStartDate) {
      message.error('End date must be greater than or equal to start date.');
      return;
    }
    const formattedLabel = `${dayjs(customStartDate).format('DD-MM-YYYY')} - ${dayjs(customEndDate).format('DD-MM-YYYY')}`;
    setSelectedDateRange('custom');
    setSelectedDateRangeLabel(formattedLabel);
    setStartDate(customStartDate);
    setEndDate(customEndDate);
    setShowCustomDateModal(false);

    const newFilters = {
      startDate: customStartDate,
      endDate: customEndDate,
      teamId,
      employeeId,
      floorId,
    };
    setCurrentPage(1);
    setAppliedFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 0, size: 10 }));
    fetchAll(0, newFilters, 10);
  };

  // Apply filters manually
  const handleApply = () => {
    const newFilters = { startDate, endDate, teamId, employeeId, floorId };
    setCurrentPage(1);
    setAppliedFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 0, size: 10 }));
    fetchAll(0, newFilters, 10);
  };

  const handleClearAll = () => {
    const defaultTeamId = !isAdmin && userTeamId ? String(userTeamId) : '';
    const defDates = resolveDateRange('today');
    setSelectedDateRange('today');
    setSelectedDateRangeLabel('Today');
    setCustomStartDate('');
    setCustomEndDate('');
    setStartDate(defDates.startDate);
    setEndDate(defDates.endDate);
    setTeamId(defaultTeamId);
    setEmployeeId('');
    setFloorId('');

    setTeams(allTeams);
    setEmployees(allEmployees);
    setCurrentPage(1);

    const clearedFilters = {
      startDate: defDates.startDate,
      endDate: defDates.endDate,
      teamId: defaultTeamId,
      employeeId: '',
      floorId: '',
    };
    setAppliedFilters(clearedFilters);
    setPagination(prev => ({ ...prev, page: 0, size: 10 }));
    fetchAll(0, clearedFilters, 10);
  };

  const handlePageChange = (page1indexed) => {
    setCurrentPage(page1indexed);
    const page0 = page1indexed - 1;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchAll(page0);
  };

  const handlePageSizeChange = (opt) => {
    const newSize = opt === 'All' ? (pagination.totalElements || 9999) : opt;
    setPagination(prev => ({ ...prev, size: newSize, page: 0 }));
    setCurrentPage(1);
    fetchAll(0, null, newSize);
  };

  // ── Excel Download ────────────────────────────────────────────────────────
  const handleDownloadExcel = async () => {
    setDownloading(true);
    message.loading({ content: 'Exporting complete analytics data...', key: 'excelExport' });
    try {
      const params = buildParams(0, 999999, appliedFilters);
      const perfRes = await dashboardApi.getEmployeePerformance(params);
      const perf = normalizeEmployeePerformance(perfRes);
      const completeData = perf.content || [];

      if (completeData.length === 0) {
        message.warning({ content: 'No data available to export', key: 'excelExport', duration: 2 });
        return;
      }

      const rows = completeData.map((row) => ({
        'Employee ID': row.employeeCode || row.employeeId || row.empId || row.code || '—',
        'Name': row.name || row.employeeName || 'Unknown',
        'Team': row.team || row.teamName || '—',
        'Confirmed': getCell(row, 'confirmedOrders', 'approvalConfirmed', 'confirmed'),
        'Verified': getCell(row, 'verified', 'complete', 'verifiedOrders'),
        'Draft': getCell(row, 'draft', 'draftOrders'),
        'Process': getCell(row, 'process', 'procces', 'processing', 'processOrders'),
        'Ready To Ship': getCell(row, 'readyToShip', 'readyToShipOrders'),
        'Pending': getCell(row, 'pending', 'pendingOrders'),
        'Hold': getCell(row, 'hold', 'onHold', 'holdOrders'),
        'Rejected': getCell(row, 'rejected', 'cancelled', 'rejectedOrders'),
        'Dispatched': getCell(row, 'dispatched', 'dispatch', 'dispatchedOrders'),
        'Shipped': getCell(row, 'shipped', 'shippedOrders'),
        'In Transit': getCell(row, 'inTransit', 'inTransitOrders'),
        'OFD': getCell(row, 'ofd', 'outForDelivery', 'ofdOrders'),
        'Delivered': getCell(row, 'delivered', 'deliveredOrders'),
        'RTO': getCell(row, 'rto', 'return', 'rtoOrders'),
        'Total': getCell(row, 'totalAmount', 'total', 'totalOrders', 'orderCount'),
        'Avg Total Amt': row.avgTotalAmount ?? row.avgTotal ?? row.avgOrderValue ?? 0,
        'Avg Verified Amt': row.avgVerifiedAmount ?? row.avgVerified ?? row.avgVerifiedOrderValue ?? 0,
      }));

      if (grandTotal) {
        rows.push({
          'Employee ID': 'GRAND TOTAL', 'Name': '', 'Team': '',
          'Confirmed': getCell(grandTotal, 'confirmedOrders', 'approvalConfirmed', 'confirmed'),
          'Verified': getCell(grandTotal, 'verified', 'complete', 'verifiedOrders'),
          'Draft': getCell(grandTotal, 'draft', 'draftOrders'),
          'Process': getCell(grandTotal, 'process', 'procces', 'processing', 'processOrders'),
          'Ready To Ship': getCell(grandTotal, 'readyToShip', 'readyToShipOrders'),
          'Pending': getCell(grandTotal, 'pending', 'pendingOrders'),
          'Hold': getCell(grandTotal, 'hold', 'onHold', 'holdOrders'),
          'Rejected': getCell(grandTotal, 'rejected', 'cancelled', 'rejectedOrders'),
          'Dispatched': getCell(grandTotal, 'dispatched', 'dispatch', 'dispatchedOrders'),
          'Shipped': getCell(grandTotal, 'shipped', 'shippedOrders'),
          'In Transit': getCell(grandTotal, 'inTransit', 'inTransitOrders'),
          'OFD': getCell(grandTotal, 'ofd', 'outForDelivery', 'ofdOrders'),
          'Delivered': getCell(grandTotal, 'delivered', 'deliveredOrders'),
          'RTO': getCell(grandTotal, 'rto', 'return', 'rtoOrders'),
          'Total': getCell(grandTotal, 'total', 'totalOrders', 'orderCount'),
          'Avg Total Amt': grandTotal.avgTotal ?? grandTotal.avgTotalAmount ?? grandTotal.avgOrderValue ?? 0,
          'Avg Verified Amt': grandTotal.avgVerified ?? grandTotal.avgVerifiedAmount ?? grandTotal.avgVerifiedOrderValue ?? 0,
        });
      }

      const ws = XLSX.utils.json_to_sheet(rows, { header: TABLE_HEADERS });
      ws['!cols'] = TABLE_HEADERS.map((h) => ({ wch: Math.max(h.length + 4, 14) }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Analytics');
      XLSX.writeFile(wb, `Analytics_Report_${appliedFilters.startDate || 'All'}_to_${appliedFilters.endDate || 'All'}.xlsx`);

      message.success({ content: 'Analytics data exported successfully!', key: 'excelExport', duration: 2 });
    } catch (err) {
      console.error('Failed to export Excel:', err);
      message.error({ content: 'Export failed. Please try again.', key: 'excelExport', duration: 3 });
    } finally {
      setDownloading(false);
    }
  };

  if (loading && Object.keys(overview).length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-10 h-10 animate-spin" style={{ color: currentTheme.color }} />
      </div>
    );
  }

  const filteredTableData = tableData.filter((row) => {
    const empName = row.name || row.employeeName || row.employee || 'Unknown';
    const teamName = row.team || row.teamName || row.teamTitle || '—';
    return empName.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
      teamName.toLowerCase().includes(tableSearchTerm.toLowerCase());
  });

  return (
    <div className={`p-6 min-h-screen transition-colors duration-300 ${!isDark ? 'bg-[#F8F9FA]' : ''}`}
      style={isDark ? { background: currentTheme.cssGradient, color: '#FFFFFF' } : {}}>

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#2D3748]'}`}>Analytics Report</h1>
        <div className="flex items-center gap-3">
          <button onClick={handleDownloadExcel} disabled={loading || downloading || tableData.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold shadow-sm transition text-white disabled:opacity-50"
            style={{ backgroundColor: currentTheme.color }}>
            {downloading ? <Loader size={15} className="animate-spin" /> : <Download size={15} />}
            {downloading ? 'Downloading...' : 'Download Excel'}
          </button>
          <button onClick={() => fetchAll(currentPage - 1)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-semibold shadow-sm transition ${isDark ? 'bg-black/20 border-white/10 text-gray-300 hover:bg-white/10' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <FilterBar
        selectedDateRange={selectedDateRange}
        selectedDateRangeLabel={selectedDateRangeLabel}
        onDateRangeSelect={handleDateRangeFilter}
        selectedTeamId={teamId} onTeamChange={handleTeamSelect}
        selectedEmployeeId={employeeId} onEmployeeChange={setEmployeeId}
        selectedFloorId={floorId} onFloorChange={handleFloorSelect}
        teams={teams} employees={employees} floors={floors}
        teamsLoading={teamsLoading} empsLoading={empsLoading}
        onApply={handleApply} onClear={handleClearAll} loading={loading}
        isAdmin={isAdmin} userTeamId={userTeamId}
        userTeamName={
          allTeams.find((t) => String(t.id) === String(userTeamId))?.teamName ||
          allTeams.find((t) => String(t.id) === String(userTeamId))?.name || ''
        }
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {STAT_KEYS.map((key) => {
          const card = overview[key] || {};
          return (
            <StatCard key={key} type={key}
              count={card.count ?? card.total ?? 0}
              totalValue={card.totalValue ?? card.totalAmount ?? 0}
              avgValue={card.avgValue ?? card.avgAmount ?? 0}
            />
          );
        })}
      </div>

      {/* Records per page */}
      <div className={`flex items-center justify-between mb-3 px-4 py-2.5 rounded-2xl border ${isDark ? 'bg-black/20 border-white/10' : 'bg-white border-gray-100'} shadow-sm`}>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Total Records: <span className="font-black" style={{ color: currentTheme.color }}>{pagination.totalElements}</span>
          </span>
          {appliedFilters.floorId && (
            <span className="text-xs font-bold px-3 py-1 rounded-full text-white flex items-center gap-1.5"
              style={{ backgroundColor: currentTheme.color }}>
              <Layers size={10} />
              Floor: {floors.find(f => String(f.id) === String(appliedFilters.floorId))?.name ||
                floors.find(f => String(f.id) === String(appliedFilters.floorId))?.floorName ||
                `Floor ${appliedFilters.floorId}`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className={`flex items-center gap-2 rounded-xl px-3 py-1.5 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
            <Search className="w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search table..."
              className={`bg-transparent border-none text-xs font-semibold focus:outline-none w-36 ${isDark ? 'text-white' : 'text-gray-800'}`}
              value={tableSearchTerm}
              onChange={e => setTableSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Show:</span>
            <div className="flex items-center gap-1">
              {PAGE_SIZE_OPTIONS.map((opt) => {
                const isActive = opt === 'All'
                  ? pagination.size >= pagination.totalElements && pagination.totalElements > 0
                  : pagination.size === opt;
                return (
                  <button key={opt} onClick={() => handlePageSizeChange(opt)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${isActive ? 'text-white border-transparent shadow-sm' : isDark ? 'bg-black/20 border-white/10 text-gray-400 hover:bg-white/10' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'}`}
                    style={isActive ? { backgroundColor: currentTheme.color } : {}}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Employee Performance Table */}
      <div className={`overflow-x-auto rounded-xl shadow-md border ${isDark ? 'border-white/10 bg-black/20' : 'border-gray-200 bg-white'}`}>
        <table className="w-full text-left border-collapse min-w-[1400px]">
          <thead>
            <tr className="text-white text-[10px] uppercase tracking-widest" style={{ backgroundColor: currentTheme.color }}>
              {TABLE_HEADERS.map((h, i, arr) => (
                <th key={h} className={`px-4 py-4 font-bold border-r border-white/20 text-center whitespace-nowrap ${i === 0 ? 'rounded-tl-xl' : ''} ${i === arr.length - 1 ? 'rounded-tr-xl border-r-0' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className={`text-[13px] ${isDark ? 'text-gray-300' : 'text-[#4A5568]'}`}>
            {loading ? (
              <tr><td colSpan={TABLE_HEADERS.length} className="py-12 text-center">
                <Loader className="inline-block w-7 h-7 animate-spin" style={{ color: currentTheme.color }} />
              </td></tr>
            ) : filteredTableData.length > 0 ? filteredTableData.map((row, index) => {
              const empCode = row.employeeCode || row.employeeId || row.empId || row.code || '—';
              const empName = row.name || row.employeeName || row.employee || 'Unknown';
              const teamName = row.team || row.teamName || row.teamTitle || '—';
              const total = getCell(row, 'total');
              const confirmed = getCell(row, 'confirmedOrders');
              const verified = getCell(row, 'verified');
              const draft = getCell(row, 'draft');
              const process = getCell(row, 'process');
              const readyToShip = getCell(row, 'readyToShip');
              const pending = getCell(row, 'pending');
              const hold = getCell(row, 'hold');
              const rejected = getCell(row, 'rejected');
              const dispatched = getCell(row, 'dispatch');
              const shipped = getCell(row, 'shipped');
              const inTransit = getCell(row, 'inTransit');
              const ofd = getCell(row, 'ofd');
              const delivered = getCell(row, 'delivered');
              const rto = getCell(row, 'rto');
              const avgTotal = row.avgTotalAmount ?? row.avgTotal ?? row.avgOrderValue ?? 0;
              const avgVerif = row.avgVerifiedAmount ?? row.avgVerified ?? row.avgVerifiedOrderValue ?? 0;
              return (
                <tr key={index} className={`${isDark ? (index % 2 === 0 ? 'bg-black/20' : 'bg-transparent') : (index % 2 === 0 ? 'bg-white' : 'bg-[#F8FBF9]')} border-b ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'}`}>
                  <td className="px-4 py-4 border-r border-gray-100 text-center font-bold text-gray-500">{empCode}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-left">
                    <button onClick={() => setSelectedEmployee(row)} className="hover:underline font-bold" style={{ color: currentTheme.color }}>{empName}</button>
                  </td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">
                    <button onClick={() => setSelectedTeam({ name: teamName, employees: teamEmployeesMap[teamName] || [] })}
                      className="text-gray-500 hover:underline font-medium transition-colors"
                      onMouseEnter={(e) => e.currentTarget.style.color = currentTheme.color}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}>
                      {teamName}
                    </button>
                  </td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{confirmed}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{verified}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{draft}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{process}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center font-semibold" style={{ color: '#0EA5E9' }}>{readyToShip}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{pending}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{hold}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{rejected}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{dispatched}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{shipped}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{inTransit}</td>
                  <td className="px-4 py-4 border-r border-gray-100 text-center">{ofd}</td>
                  <td className="px-4 py-4 border-r border-white/5 text-center">{delivered}</td>
                  <td className="px-4 py-4 border-r border-white/5 text-center">{rto}</td>
                  <td className={`px-4 py-4 border-r border-white/5 text-center font-bold ${isDark ? 'text-white' : 'text-[#2D3748]'}`}>{total}</td>
                  <td className="px-4 py-4 border-r border-white/5 text-center font-medium">₹ {fmtAvg(avgTotal)}</td>
                  <td className="px-4 py-4 text-center font-medium">₹ {fmtAvg(avgVerif)}</td>
                </tr>
              );
            }) : (
              <tr><td colSpan={TABLE_HEADERS.length} className="px-6 py-8 text-center text-gray-500 font-medium">No results found matching "{tableSearchTerm}".</td></tr>
            )}

            {/* Grand Total Row */}
            {grandTotal && (
              <tr className={`font-bold border-t-2 ${isDark ? 'bg-black/40 text-white' : 'bg-[#F1F5F9] text-[#2D3748]'}`} style={{ borderTopColor: `${currentTheme.color}4d` }}>
                <td className="px-4 py-5 italic border-r border-white/5 font-bold text-left" colSpan="3">Grand Total : {getCell(grandTotal, 'totalAmount')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'approvalConfirmed')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'verified')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'draft')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'process')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center font-semibold" style={{ color: '#0EA5E9' }}>{getCell(grandTotal, 'readyToShip')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'pending')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'hold')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'rejected')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'dispatch')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'shipped')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'inTransit')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center">{getCell(grandTotal, 'ofd')}</td>
                <td className="px-4 py-5 border-r border-white/5 text-center">{getCell(grandTotal, 'delivered')}</td>
                <td className="px-4 py-5 border-r border-white/5 text-center">{getCell(grandTotal, 'rto')}</td>
                <td className={`px-4 py-5 border-r border-white/5 text-center font-extrabold ${isDark ? 'text-white' : 'text-black'}`}>{getCell(grandTotal, 'total', 'totalOrders')}</td>
                <td className="px-4 py-5 border-r border-gray-200 text-center font-bold" style={{ color: currentTheme.color }}>₹ {fmtAvg(grandTotal.avgTotal ?? grandTotal.avgTotalAmount ?? 0)}</td>
                <td className="px-4 py-5 text-center font-bold" style={{ color: currentTheme.color }}>₹ {fmtAvg(grandTotal.avgVerified ?? grandTotal.avgVerifiedAmount ?? 0)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={currentPage} totalItems={pagination.totalElements} itemsPerPage={pagination.size} onPageChange={handlePageChange} />

      {/* ── Custom Date Modal ── */}
      {showCustomDateModal && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCustomDateModal(false);
          }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <div className={`rounded-2xl shadow-2xl w-full max-w-md p-6 ${isDark ? 'bg-[#1e1e1e] text-white border border-white/10' : 'bg-white text-gray-800'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Select Custom Date Range</h3>
              <button
                onClick={() => setShowCustomDateModal(false)}
                className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-500 mb-2">Start Date – End Date</label>
              <RangePicker
                value={
                  customStartDate && customEndDate
                    ? [dayjs(customStartDate), dayjs(customEndDate)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setCustomStartDate(dates[0].format('YYYY-MM-DD'));
                    setCustomEndDate(dates[1].format('YYYY-MM-DD'));
                  } else {
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }
                }}
                format="DD-MM-YYYY"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '10px' }}
                className="w-full text-base"
                size="large"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCustomDateModal(false)}
                className={`px-5 py-2 text-sm font-semibold rounded-xl transition-colors ${isDark ? 'bg-white/10 text-gray-300 hover:bg-white/20' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleCustomDateApply}
                disabled={!customStartDate || !customEndDate}
                className="px-5 py-2 text-sm font-semibold text-white rounded-xl transition-colors hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <TeamMembersModal
        isOpen={!!selectedTeam && !selectedEmployee}
        onClose={() => setSelectedTeam(null)}
        teamName={selectedTeam?.name}
        employees={selectedTeam?.employees || []}
        onEmployeeClick={(emp) => setSelectedEmployee(emp)}
      />
      <EmployeeDetailModal
        isOpen={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        employee={selectedEmployee}
      />
    </div>
  );
};

export default Analytics;